import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { isValidPixelId, loadPixel, trackEvent } from '@/lib/facebookPixel';
import { buildSubdomain } from '@/lib/domain';
import EventCounter from './components/EventCounter';
import TrackingEventsCard from './components/TrackingEventsCard';
import InstallGuide from './components/InstallGuide';

type TestStatus = 'idle' | 'running' | 'success' | 'error';

export default function FacebookPixelPage() {
  const { user } = useAuth();
  const [pixelId, setPixelId] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<TestStatus>('idle');
  const [testMessage, setTestMessage] = useState<string | null>(null);

  const formatError = pixelId.trim() !== '' && !isValidPixelId(pixelId);
  const storeUrl = user?.user_name ? buildSubdomain(user.user_name) : '';

  const fetchConfig = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { data, error } = await supabase
        .from('facebook_pixel_config')
        .select('pixel_id, enabled')
        .eq('idcommerce', user.id)
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setPixelId(data.pixel_id || '');
        setEnabled(data.enabled ?? true);
      }
    } catch {
      setLoadError('Impossible de charger la configuration. Réessayez.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    if (!user) return;
    if (!pixelId.trim()) {
      setSaveError('Veuillez renseigner un Pixel ID.');
      return;
    }
    if (!isValidPixelId(pixelId)) {
      setSaveError('Le Pixel ID doit contenir 15 ou 16 chiffres.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const { error } = await supabase
        .from('facebook_pixel_config')
        .upsert(
          {
            idcommerce: user.id,
            pixel_id: pixelId.trim(),
            enabled,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'idcommerce' }
        );

      if (error) throw error;
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setSaveError('Impossible d\u2019enregistrer la configuration. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!pixelId.trim()) {
      setTestStatus('error');
      setTestMessage('Renseignez d\u2019abord un Pixel ID.');
      return;
    }
    if (!isValidPixelId(pixelId)) {
      setTestStatus('error');
      setTestMessage('Pixel ID invalide : il doit contenir 15 ou 16 chiffres.');
      return;
    }

    setTestStatus('running');
    setTestMessage('Chargement du script Facebook Pixel...');

    // Charge réellement le script fbevents.js, fait l'init et envoie un
    // événement de test (identique au bouton « Tester » de Meta).
    const ok = loadPixel(pixelId);
    trackEvent('PageView');

    await new Promise((r) => setTimeout(r, 1200));

    if (ok) {
      setTestStatus('success');
      setTestMessage('Pixel chargé et événement de test envoyé. Vérifiez-le dans Facebook Events Manager.');
    } else {
      setTestStatus('error');
      setTestMessage('Impossible de charger le pixel. Vérifiez votre Pixel ID.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* En-tête */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
            <i className="ri-facebook-circle-line mr-2 text-accent-600"></i>
            Facebook Pixel
          </h2>
          <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
            Suivez les visites et les conversions de votre boutique sur toutes les pages publiques,
            pour créer des audiences et mesurer vos publicités Meta.
          </p>
        </div>
        {storeUrl && (
          <a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold hover:bg-background-200/70 transition-colors no-underline whitespace-nowrap self-start"
          >
            <i className="ri-external-link-line"></i>
            Voir ma boutique
          </a>
        )}
      </div>

      {/* Erreur de chargement */}
      {loadError && (
        <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-red-50 text-red-700 text-sm rounded-lg">
          <i className="ri-error-warning-line"></i>
          <span className="flex-1">{loadError}</span>
          <button onClick={fetchConfig} className="font-semibold whitespace-nowrap cursor-pointer hover:text-red-900">
            Réessayer
          </button>
        </div>
      )}

      <div className="space-y-5">
        {/* Vue d'ensemble */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className={`w-9 h-9 rounded-md flex items-center justify-center mb-2.5 ${enabled && pixelId ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-500'}`}>
              <i className="ri-radar-line"></i>
            </div>
            <p className={`text-lg font-bold font-heading ${enabled && pixelId ? 'text-accent-600' : 'text-foreground-700'}`}>
              {enabled && pixelId ? 'Actif' : pixelId ? 'En pause' : 'Non configuré'}
            </p>
            <p className="text-xs text-foreground-500 mt-0.5">Statut du suivi</p>
          </div>
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center mb-2.5">
              <i className="ri-pages-line"></i>
            </div>
            <p className="text-lg font-bold font-heading text-foreground-950">Toutes les pages</p>
            <p className="text-xs text-foreground-500 mt-0.5">Couverture du site public</p>
          </div>
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center mb-2.5">
              <i className="ri-flashlight-line"></i>
            </div>
            <p className="text-lg font-bold font-heading text-foreground-950">6 événements</p>
            <p className="text-xs text-foreground-500 mt-0.5">Suivis sans code</p>
          </div>
        </div>

        {/* Statut */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground-800">Activer le suivi</h3>
              <p className="text-xs text-foreground-500 mt-0.5">
                {enabled
                  ? 'Le pixel est actif : il est injecté sur toutes les pages publiques de votre site.'
                  : 'Le pixel est désactivé : aucun suivi n’est effectué sur votre site.'}
              </p>
            </div>
            <button
              onClick={() => setEnabled(!enabled)}
              aria-label={enabled ? 'Désactiver le pixel' : 'Activer le pixel'}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                enabled ? 'bg-accent-500' : 'bg-background-200'
              }`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>

        {/* Pixel ID + actions */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <label htmlFor="fb-pixel-id" className="block text-sm font-semibold text-foreground-800 mb-2">
            Facebook Pixel ID
          </label>
          <input
            id="fb-pixel-id"
            type="text"
            inputMode="numeric"
            value={pixelId}
            onChange={(e) => setPixelId(e.target.value.replace(/\D/g, ''))}
            placeholder="1234567890123456"
            maxLength={16}
            className={`w-full px-4 py-3 bg-background-100 border rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none font-mono transition-colors ${
              formatError ? 'border-red-400 focus:border-red-400' : 'border-background-200/70 focus:border-primary-300'
            }`}
          />
          {formatError ? (
            <p className="text-xs text-red-500 mt-1.5">
              Format invalide : le Pixel ID doit contenir 15 ou 16 chiffres.
            </p>
          ) : (
            <p className="text-xs text-foreground-400 mt-1.5">
              Trouvez votre Pixel ID dans Facebook Events Manager → Sources de données.
            </p>
          )}

          {saveError && (
            <div className="mt-3 flex items-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 text-sm rounded-lg">
              <i className="ri-error-warning-line"></i>
              {saveError}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enregistrement...
                </>
              ) : (
                <>
                  <i className="ri-check-line"></i>
                  Enregistrer
                </>
              )}
            </button>
            <button
              onClick={handleTest}
              disabled={testStatus === 'running' || !pixelId.trim()}
              className="flex items-center gap-2 px-5 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className={`ri-radar-line ${testStatus === 'running' ? 'animate-pulse' : ''}`}></i>
              Tester le pixel
            </button>
            {saved && (
              <span className="text-sm text-accent-600 flex items-center gap-1">
                <i className="ri-checkbox-circle-line"></i>
                Configuration sauvegardée
              </span>
            )}
          </div>

          {testMessage && (
            <p className={`mt-3 text-sm flex items-start gap-2 ${
              testStatus === 'success' ? 'text-accent-600'
              : testStatus === 'error' ? 'text-red-500'
              : 'text-foreground-500'
            }`}>
              <i className={`${
                testStatus === 'success' ? 'ri-checkbox-circle-line'
                : testStatus === 'error' ? 'ri-error-warning-line'
                : 'ri-loader-4-line animate-spin'
              } mt-0.5`}></i>
              <span>{testMessage}</span>
            </p>
          )}
        </div>

        {/* Événements */}
        <TrackingEventsCard />

        {/* Compteur d'événements captés */}
        <EventCounter storeId={user.id} />

        {/* Mode d'emploi */}
        <InstallGuide pixelId={pixelId} enabled={enabled} />
      </div>
    </div>
  );
}